import { useMemo } from 'react';
import { Link } from 'react-router';
import { useConversation } from '../../context';
import type { Conversation, Participant } from '../../types';

/**
 * Banner shown when a company inbox enquiry was sent to a specific network
 * branch. Only visible from the network owner company inbox.
 * @see {@link ChatBox}
 */
export function ChatBoxBranchEnquiryBanner() {
    const { professionalID, conversation, selfParticipant } = useConversation();
    const branchProfessional = useMemo(() => {
        return getBranchProfessional({
            conversation,
            professionalID,
            selfParticipant,
        });
    }, [conversation, professionalID, selfParticipant]);

    if (!branchProfessional) {
        return null;
    }

    return (
        <div className="flex items-center gap-[8px] rounded-[8px] bg-gray-100 px-[12px] py-[8px] text-[13px] leading-normal text-black/80">
            {branchProfessional.CompanyLogo && (
                <img
                    src={branchProfessional.CompanyLogo}
                    alt={branchProfessional.Title}
                    className="size-[28px] shrink-0 rounded-full object-contain"
                    style={{
                        backgroundColor:
                            branchProfessional.LogoBackgroundColor ?? 'white',
                    }}
                />
            )}
            <div className="min-w-0 flex-1">
                <span>Enquiry was sent to </span>
                <Link
                    to={branchProfessional.Link}
                    className="font-medium text-black hover:underline"
                >
                    {branchProfessional.Title}
                </Link>
            </div>
        </div>
    );
}

/**
 * Get the branch professional for this enquiry conversation. Returns `null` if
 * not in a company inbox context or the company inbox professional is not the network owner.
 */
function getBranchProfessional({
    conversation,
    professionalID,
    selfParticipant,
}: {
    conversation: Conversation | undefined;
    professionalID: number | null;
    selfParticipant: Participant | null;
}) {
    if (!professionalID || professionalID <= 0) {
        return null;
    }
    if (conversation?.__typename !== 'EnquiryConversation') {
        return null;
    }
    if (
        selfParticipant?.__typename !== 'EnquiryParticipant' ||
        selfParticipant.IsCollaborator
    ) {
        return null;
    }
    const branchParticipant = conversation?.Participants.find(
        ({ Identity, IsCollaborator }) =>
            Identity.__typename === 'Professional' && IsCollaborator,
    );
    return branchParticipant?.Identity.__typename === 'Professional'
        ? branchParticipant.Identity
        : null;
}
